import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Power, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", org_type: "Hospital Group", contact_email: "", contact_phone: "", address: "", subscription_plan: "Trial", subscription_status: "active" };
const ORG_TYPES = ["Hospital Group", "Single Hospital", "Clinic Chain", "Diagnostic Network", "Integrated Care Provider"];

export default function Organisations() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/platform/organisations").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o, contact_email: o.contact_email || "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, contact_email: form.contact_email || null };
      if (editing) await api.put(`/platform/organisations/${editing.id}`, payload);
      else await api.post("/platform/organisations", payload);
      toast.success(editing ? "Organisation updated" : "Organisation created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (o) => {
    try { await api.patch(`/platform/organisations/${o.id}/status`); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="organisations-page">
      <PageHeader title="Organisations" subtitle="Independent healthcare tenants on the platform. Each organisation's data is fully isolated."
        actions={<Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800" data-testid="create-organisation-button"><Plus className="h-4 w-4 mr-1.5" /> New Organisation</Button>} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead data-testid="th-org-name">Organisation</TableHead>
            <TableHead>Type</TableHead><TableHead>Facilities</TableHead><TableHead>Users</TableHead>
            <TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id} className="hover:bg-slate-50" data-testid={`org-row-${o.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-md bg-slate-100 text-slate-500 grid place-items-center"><Building2 className="h-4 w-4" /></span>
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2">{o.name}{o.is_demo && <span className="text-[10px] font-mono uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">demo</span>}</div>
                      <div className="text-xs text-slate-400 font-mono">{o.id.slice(0, 8)}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{o.org_type}</TableCell>
                <TableCell className="tabular-nums">{o.facilities_count}</TableCell>
                <TableCell className="tabular-nums">{o.users_count ?? 0}</TableCell>
                <TableCell className="text-sm text-slate-600">{o.subscription_plan}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`org-actions-${o.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(o)} data-testid={`org-edit-${o.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggle(o)} data-testid={`org-toggle-${o.id}`}><Power className="h-4 w-4 mr-2" /> {o.status === "active" ? "Deactivate" : "Activate"}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No organisations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>)}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="organisation-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Organisation" : "New Organisation"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Organisation name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} data-testid="org-type-select">
                  {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label>Subscription plan</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.subscription_plan} onChange={(e) => setForm({ ...form, subscription_plan: e.target.value })} data-testid="org-plan-select">
                  {["Trial", "Standard", "Professional", "Enterprise"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="org-email-input" /></div>
              <div className="space-y-1.5"><Label>Contact phone</Label><Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="org-phone-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Registered address</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="org-address-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-blue-700 hover:bg-blue-800" data-testid="org-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
